import { QNode, QThenFunction, QStep, QStateObject } from "./QFlow";

type FormNodeOpts = {
    terminal : boolean,
    nextStr : string,
    allowBack : boolean,
    backStr : string
}

/**
 * This node implements logic for simple forms. Each 
 */
class FormNode extends QNode {

    private content : HTMLElement;
    private nbtn : HTMLButtonElement;
    private bbtn : HTMLButtonElement;
    private opts : FormNodeOpts;

    constructor(content: HTMLElement, 
        {
            terminal = false,
            nextStr = "Next",
            allowBack = true,
            backStr = "Back"
        } : Partial<FormNodeOpts>
    ) {
        super();
        this.content = content;
        this.opts = {
            terminal, nextStr, allowBack, backStr
        };

        // Set up buttons
        if(this.opts.allowBack) {
            this.content.appendChild(this.bbtn = document.createElement('button'));
            this.bbtn.innerHTML = this.opts.backStr;
        }

        if(!this.opts.terminal) {
            this.content.appendChild(this.nbtn = document.createElement('button'));
            this.nbtn.innerHTML = this.opts.nextStr;
        }
    }

    /**
     * Get input elements in this form
     */
    protected getInputs() : { input: HTMLInputElement, name: string }[] {
        var els = this.content.querySelectorAll("input, select");

        var out : { input: HTMLInputElement, name: string }[] = [];

        for(var e of els) {
            var name = e.getAttribute('name');

            // This should never happen, but it reassures the static type checker
            if(!(e instanceof HTMLInputElement)) {
                continue;
            }

            if(name == null) {
                //console.log("Warning: Form element has no name", e);
                continue;
            }
            out.push({
                name: name,
                input: e
            });
        }

        return out;
    }

    /** 
     * Find state in this form
     * We look for form elements, basically
     */
    protected getState() : QStateObject {

        var els = this.getInputs();
        var state = {};

        //console.debug("Found elements in FormNode", els);

        for(var input of els) {
            var value;

            // TODO: This might not work with radios?
            if(input.input instanceof HTMLSelectElement || input.input instanceof HTMLInputElement) {

                if(input.input.getAttribute('type') == 'radio') {
                    if(!input.input.checked) {
                        //console.debug("Radio button is not checked, skipping");
                        continue;
                    } else {
                        value = input.input.value;
                    }
                } else {
                    value = input.input.value;
                }
                
            } else {
                //console.debug("Unknown form element", input);
                continue;
            }

            state[input.name] = value;
        }

        return state;
    }

    /**
     * Apply the given state to elements in this form
     */
    protected applyState(state : QStateObject) {
        var els = this.getInputs();

        for(var input of els) {
            if(typeof state[input.name] !== 'undefined') {

                // Radio buttons work a bit differently
                if(input.input.getAttribute('type') == 'radio') {
                    input.input.checked = state[input.name] == input.input.value;
                } else { // Everything else works the same
                    input.input.value = state[input.name].toString();
                }
            }
        }
    }

    /**
     * Initialise this node with the given state and callback function
     * @param state: The incoming state, which this node may modify and then pass back using the then() callback
     * @param then: A callback to update state and move to another step (see QThenFunction)
     */
    go(state: QStateObject, then: QThenFunction) {
        //console.log(this);

        // Focus our content element
        this.content.scrollIntoView({ behavior: 'auto' });

        // Restore state
        this.applyState(state);

        // Set up next button
        if(this.nbtn) {
            this.nbtn.onclick = () => {
                var newstate = this.getState();

                for(var k in newstate) {
                    state[k] = newstate[k];
                }

                then(state);
            }
        }

        //  Set up a back button if enabled
        if(this.bbtn) {
            this.bbtn.onclick = () => {
                then({}, -1);
            }
        }

        // TODO: Also do history stuff so back button works as back
        // TODO: If back is disabled, prevent back button from doing anything?
    }

    isVisible() {
        return true;
    }
}

export default FormNode;